import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useIntl} from "react-intl";
import {useDispatch, useSelector} from "react-redux";
import {Button, TimeFrame, useTimeFrameComponent} from "@gpstrackit/gtc-react-controls";
import {exportReportBehaviors, openModalExportBehavior} from "./redux/slice";
import {BEHAVIOR_DATE_TODAY, formatEntities, getOptionsSelectorDate} from "Modules/behavior/views/report/redux/helpers";
import {openModalBehaviorAdvanceFilter} from "Modules/behavior/views/report/modals/filter/redux/slice";
import moment from "moment";
import SeverityOptions from "Modules/alerts/common/components/SeverityOptions";
import _ from "lodash";
import {NotificationManager} from "Components/Notifications";
import {Form, Select} from 'Components';
import Modal, {ModalBody, ModalFooter} from "Components/Modal";
import {useNewEntityTag} from "Core/hooks";
import EntityUnitDriverSelector from "Modules/.common/components/EntityUnitDriverSelector";

const ExportBehaviorReport = (props) => {
    const {preferencesRef, customcolumns, sort} = props;
    const dispatch = useDispatch();
    const {messages} = useIntl();
    const formExport = useRef();

    const optionsSelectorDate = useMemo(() => getOptionsSelectorDate(messages), [messages]);
    const optionsFormatSelect = useMemo(() => [
        {id: "csv", label: "CSV"},
        {id: "pdf", label: "PDF"},
    ], []);
    const optionsSelectReportType = useMemo(() => [
        {id: "simple", label: messages['exportBehavior_simpleReport']},
        {id: "detailed", label: messages['exportBehavior_detailedReport']},
    ], [messages]);

    const {open} = useSelector(state => state.reportExportBehaviorsRedux);
    const {filter, hasFilters} = useSelector(state => state.reportBehaviorAdvanceFilterRedux);
    const user = useSelector((state) => state.securityRedux.user);
    const lang = useSelector(state => state.commomRedux.language);
    const [formatSelected, setFormatSelected] = useState({id: "csv", label: "CSV"})
    const [reportTypeSelected, setReportTypeSelected] = useState({id: "detailed", label: messages['exportBehavior_detailedReport']})
    const [error, setError] = useState('');
    const [activeCalendar, setActiveCalendar] = useState(false)
    const [loadingEntity, setLoadingEntity] = useState(false)

    const timeFrame = useTimeFrameComponent(optionsSelectorDate, BEHAVIOR_DATE_TODAY)

    const entityTags = useNewEntityTag(
        ["Unit", "Driver"],
        messages['reportsMainRT_viewReportBy'],
        "calc(100vh - 320px)",
        messages['reportsMainRT_runReportFor'],
        filter?.entities.length
            ? filter?.entities[0].name === "Tag" || filter?.option === "tags"
                ? "tags"
                : "entities"
            : "all",
        filter?.entity ? filter?.entity : "Unit"
    )

    const changePreferences = useCallback((newValues) => {
        const hasFilters =
            (newValues?.dateOption?.id && newValues?.dateOption?.id !== "today") ||
            newValues?.entities?.length > 0 ||
            (newValues?.risk != null && newValues?.risk !== "All") ||
            (newValues.entity != null && newValues.entity !== "Unit");
        dispatch(
            openModalBehaviorAdvanceFilter({
                hasFilters: hasFilters,
                filter: newValues,
            })
        );

        const oldPreferences =
            preferencesRef?.current?.getPreferences("User", "behaviorView") || {};
        if (!_.isEqual(newValues, oldPreferences)) {
            const currentPreferences = {...oldPreferences, ...newValues};
            preferencesRef.current.savePreference(
                "User",
                "behaviorView",
                currentPreferences
            );
        }
    }, []);

    useEffect(() => {
        if (open){
            setTimeout(() => {
                entityTags.onChange(filter?.entities || []);
                timeFrame.defaultvalue(filter?.dateOption?.id || BEHAVIOR_DATE_TODAY);
                timeFrame.onChangeDateRange(
                    moment(filter?.dateRange?.start) ?? moment().startOf("d"),
                    moment(filter?.dateRange?.end) ?? moment().endOf("d")
                );
            }, 100);
        }

    }, [open])

    const handleClose = () => {
        dispatch(openModalExportBehavior({open: !open}));
    };

    const handleSuccess = () => {
        dispatch(exportReportBehaviors({filter, export: {format: formatSelected.id, type: reportTypeSelected.id}, user, customcolumns, lang, sort}))
        NotificationManager.success(
            messages['exportBehavior_exportGenerated'],
            messages['success'],
            3000,
            null,
            null,
            'Success',
        );
        handleClose()
    };

    function onChangueItems(items, entity) {
        entityTags.onChange(items)
        changePreferences({
            ...filter, entity: entity ?  entity?.id : filter.entity, entities: formatEntities(items)
        })
    }

    return (
        <Modal
            title={messages["exportBehavior_exportBehavior"]}
            size="small"
            loading={loadingEntity}
            open={open}
            position="right"
            handleClose={handleClose}
        >
            <ModalBody>
                <div style={{padding: "12px 25px"}} className={'advanced-filters_modal'}>
                <Form ref={formExport} onValidSubmit={() => {
                    if (formatSelected && reportTypeSelected) {
                        handleSuccess()
                    }
                }}>
                    <Select
                        label={messages["exportBehavior_fileFormat"]}
                        id="selectFormat"
                        options={optionsFormatSelect}
                        onChange={(value) => {
                            setFormatSelected(value);
                        }}
                        value={formatSelected}
                        isClearable
                        isSearchable
                        required
                        requiredMessage={messages['required_field']}
                        textField="label"
                        valueField="id"
                    />
                    <div style={{marginBottom: 8, marginTop: 10}}>
                        <Select
                            label={messages["exportBehavior_reportType"]}
                            id="selectReportType"
                            options={optionsSelectReportType}
                            onChange={(value) => {
                                setReportTypeSelected(value);
                            }}
                            value={reportTypeSelected}
                            isClearable
                            isSearchable
                            required
                            requiredMessage={messages['required_field']}
                            textField="label"
                            valueField="id"
                        />
                    </div>
                </Form>
                    {
                        open &&
                        <div style={{marginBottom: 8, marginTop: 10}}>
                            <div style={{marginTop: 10}}>
                                <EntityUnitDriverSelector
                                    {...entityTags}
                                    module={'report-module'}
                                    onChangeEntity={(newEntity) => {
                                        changePreferences({
                                            ...filter,
                                            entity: newEntity.id
                                        })
                                        entityTags.onOptionChange({id: 'all'})
                                        entityTags.onChangeEntity(newEntity)
                                    }}
                                    onOptionChange={(newOption) => {
                                        entityTags.onOptionChange(newOption)
                                        changePreferences({
                                            ...filter,
                                            option: newOption.id
                                        })
                                    }}
                                    onChange={onChangueItems}
                                    entityLabelClass='report-setup-option-label'
                                    entityOptionLabelClass='report-setup-type-title'
                                    entityTagPickerContainerClass='report-setup-container-tag-picker'
                                />
                            </div>
                        </div>
                    }
                    <div style={{marginTop: 20}}>
                        <div className="time-frame-container">
                            <TimeFrame
                                {...timeFrame}
                                label={messages['behavior_timeFrame']}
                                placeholder={messages['behavior_timeFrame']}
                                maxDate={moment()}
                                filterSwitch={false}
                                customRangeDate={90}
                                withTime={true}
                                openDateRangePickerModalAutomatically={activeCalendar}
                                dateFormat={user?.dateformat || "MM/DD/YYYY hh:mm A"}
                                onChangeSelect={(option) => {
                                    const selectedTime = optionsSelectorDate.find(range => range.id === option.id);
                                    if (option) {
                                        timeFrame.onChangeSelect(option);
                                        changePreferences({
                                            ...filter,
                                            dateOption: option,
                                            dateRange: selectedTime.dateRange
                                        })
                                        setActiveCalendar(option.id === 'custom')
                                    }
                                }}
                                onChangeDateRange={(start, end) => {
                                    const startM = moment(start).startOf('day');
                                    const endM = moment(end).endOf('day');
                                    if (endM.diff(startM, 'days') > 90) {
                                        setError(messages['behavior_errorDateRange'])
                                    } else {
                                        timeFrame.onChangeDateRange(startM, endM);
                                        changePreferences({
                                            ...filter,
                                            entity: !filter?.entity ? 'Unit' : filter.entity,
                                            dateOption: timeFrame.option,
                                            dateRange: { start: startM.toDate(), end: endM.toDate() }
                                        })
                                        setError('')
                                    }
                                }}
                            />
                        </div>
                        <div style={{width: '100%'}}>
                            {error && <small className="select-error text-danger">{error}</small>}
                        </div>
                    </div>
                    <div style={{marginTop: 120, marginBottom: 10, textAlign: 'center'}}>
                        <SeverityOptions
                            customStyle={{justifyContent: 'center', padding: "10px 0px"}}
                            label={messages['behavior_riskFactors']}
                            onChange={(newSeverity) => {
                                let severity = newSeverity;
                                if (newSeverity === filter?.risk) {
                                    severity = null;
                                }
                                changePreferences({...filter, risk: severity})
                            }}
                            selectedButtons={[{id: filter?.risk}]}
                            all={true}
                            messages={messages}
                        />
                    </div>
                </div>

            </ModalBody>
            <ModalFooter>
                <div>
                    <Button
                        variant="secondary"
                        style={{marginRight: "5px"}}
                        onClick={() => handleClose()}
                        disabled={loadingEntity}
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={(e) => {
                            if (e) e.preventDefault();
                            formExport.current.submit();
                        }}
                        disabled={loadingEntity}
                    >
                        {messages["export"]}
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default ExportBehaviorReport;
